/**
 * @file novel.ts
 * @author liuchen(loocliu@tencent.com)
 */

import { MessagePlugin } from 'tdesign-react';

import { AuditStatusType } from '@/const/status';
import { RichMediaValue } from '@/interface/media';
import { INovel, INovelHome, INovelHomeItem, NovelHomeType } from '@/interface/novel';
import { ChannelCheckedType, ChannelNovelSortType, NovelSearchType } from '@/pages/novel/ugcList/search/const';
import { transformObjectDeep } from '@/utils/tans/object';

import { CgiErrorCode, CgiResp } from './cgi';
import { API_PREFIX_AI, API_PREFIX_AI_NOVEL, API_PREFIX_PLOT, request } from './request';

export interface ICreateCompleteNovelRequest {
  userId: string;
  novelSpaceId: string;
  inspiration: string;
  concept: string;
  character: string;
  briefOutline: string;
  sequenceOutline: string;
  au: string;
  era: string;
  ocSetting: string;
  endingType: string;
  cpOrientation: string;
  novelType: string;
  style: string;
}

/**
 * 创建完整故事
 */
export const createCompleteNovel = async (req: ICreateCompleteNovelRequest) => {
  const res = await request<CgiResp<{ novel_id: string }>>(`${API_PREFIX_AI_NOVEL}/CreateCompleteNovel`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
  return res.data.novel_id;
};

interface IUploadNovelCoverRequest {
  userId: string;
  novelId: string;
  novelCoverUrl: string;
}

/**
 * 上传小说封面
 */
export const uploadNovelCover = async (req: IUploadNovelCoverRequest) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/UpdateNovelCover`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

interface IUploadSeqImgReq {
  userId: string;
  novelId: string;
  imgUrl: string;
}

/**
 * 上传小说第一序列图
 */
export const uploadSeqImg = async (req: IUploadSeqImgReq) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/UpdateFirstSeqImg`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

/**
 * 编辑小说标签
 */
export const editNovelTag = async (req: { novelId: string; tagIds: string[] }) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/NovelTagPost`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

interface ISetRecommendNovelRequest {
  userId: string;
  novelId: string;
  isRecommend: boolean;
  recommendType: NovelType;
}

/**
 * 配置推荐小说
 */
export const setRecommendNovel = async (req: ISetRecommendNovelRequest) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/SetRecommendNovel`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};


/**
 * 编辑第一节
 */
export const updateFirstSeqPlot = async (req: { novelId: string; plot: string }) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/UpdateFirstSeqPlot`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

/**
 * 编辑小说名称
 * @param req
 */
export const updateNovelName = async (req: { userId: string; novelId: string; novelName: string }) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/UpdateNovelName`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

interface IGetCompleteNovelListRequest {
  userId: string;
  page: number;
  size: number;
  novelSpaceId?: string;
  isRecommend?: boolean;
}

export enum NovelType {
  INTERACT = 1,
  COMPLETE
}

export interface IGetCompleteNovelListResponse {
  list: {
    novelId: string;
    progress: number;
    novelCoverUrl: string;
    firstSeqImgUrl: string;
    novelName: string;
    isRecommend: boolean;
    req: ICreateCompleteNovelRequest;
    type: NovelType;
    firstSeqPlot: string;
    tagIds: string[];
  }[];
  totalCnt: number;
}

/**
 * 拉取完整故事列表
 */
export const getCompleteNovelList = async (req: IGetCompleteNovelListRequest) => {
  const res = await request<CgiResp<IGetCompleteNovelListResponse>>(`${API_PREFIX_AI_NOVEL}/GetCompleteNovelList`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
  const transRes = transformObjectDeep(res.data, 'camel', true) as IGetCompleteNovelListResponse;
  return {
    ...transRes, totalCnt: Number(transRes.totalCnt) || 0,
  };
};

interface INovelSpace {
  id: string;
  name: string;
}

/** 世界（World）简要信息 */
export interface IWorld {
  id: string;
  name: string;
}

/**
 * 拉取世界列表（注意：指的是C端的世界，不是世界观）
 */
export const getWorldList = async (page = 1, size = 999): Promise<IWorld[]> => {
  const res = await request<CgiResp<{ msg: string; data: { list: IWorld[]; hasMore: boolean } }>>(`${API_PREFIX_PLOT}/world/get_list?page=${page}&size=${size}`);
  const data = transformObjectDeep(res.data?.data, 'camel') as { list: IWorld[]; hasMore: boolean };
  return data?.list || [];
};

/**
 * 拉取世界观列表
 */
export const getNovelSpaces = async () => {
  const res = await request<CgiResp<{ list: INovelSpace[] }>>(`${API_PREFIX_AI_NOVEL}/ListNovelSpace`, {
    method: 'POST',
    body: {
      page: 1,
      size: 999,
    },
  });
  return (transformObjectDeep(res.data, 'camel') as { list: INovelSpace[] }).list;
};

/**
 * 创建/编辑 标签
 */
export const postTag = async (req: { id?: string; name: string; bgImg: string; }) => {
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_PLOT}/tag_post`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
  return res.data.id;
};

/**
 * 删除 标签
 */
export const deleteTag = async (req: string) => {
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_PLOT}/tag_delete`, {
    method: 'POST',
    body: { ids: [req] },
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
};

export interface ITag {
  id: string;
  name: string;
  bgImg: string;
  bgm: string;
}

/**
 * 查询 标签列表
 */
export const getTags = async (req: { page?: number; size?: number }) => {
  const res = await request<CgiResp<{ list: ITag[] }>>(`${API_PREFIX_PLOT}/tag_list?page=${req.page || 1}&size=${req.size || 999}`);
  if (!res.data) {
    throw new Error(res.msg);
  }
  return (transformObjectDeep(res.data, 'camel') as { list: ITag[] }).list;
};

/**
 * 二级导航标签
 */
export const setSecondNavigations = async (req: string[]) => {
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_PLOT}/second_navigation_tag_post`, {
    method: 'POST',
    body: { ids: req },
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
  return res.data.id;
};

/**
 * 查询 二级导航标签列表
 */
export const getSecondNavigations = async () => {
  const res = await request<CgiResp<{ list: ITag[] }>>(`${API_PREFIX_PLOT}/second_navigation_tag_list`);
  if (!res.data) {
    throw new Error(res.msg);
  }
  return res.data.list;
};

export interface IArgOptions {
  au: string[];
  era: string[];
  endingType: string[];
  cpOrientation: string[];
  novelLength: string[];
}

export const getArgOptions = async () => {
  const res = await request<CgiResp<IArgOptions>>(`${API_PREFIX_AI_NOVEL}/GetSelfSetArgs`, {
    method: 'POST',
  });
  if (!res.data) {
    throw new Error(res.msg);
  }
  return transformObjectDeep(res.data, 'camel', true) as IArgOptions;
};

interface IGetNovelHomeListRes {
  list: INovelHome[];
  pageinfo: {
    total: number;
    page: number;
    size: number;
  }
}

/**
 * 拉取列表
 * @param id
 * @returns
 */
export const getNovelHomeList = async ({
  page = 1,
  size = 10,
  type = NovelHomeType.APP,
}: {
  page?: number;
  size?: number;
  type: NovelHomeType;
}) => {
  const res = await request(`${API_PREFIX_AI_NOVEL}/GetHomeDataList`, {
    method: 'POST',
    body: transformObjectDeep({
      page,
      size,
      homeDatatype: type,
      isUsed: 3,
    }, 'snake', true),
  });
  const transRes = transformObjectDeep(res?.data, 'camel', true) as IGetNovelHomeListRes;
  return {
    list: transRes.list,
    pageInfo: {
      total: transRes.pageinfo?.total || 0,
      current: transRes.pageinfo?.page || 1,
      pageSize: transRes.pageinfo?.size || 10,
    },
  };
};


/**
 * 更改配置状态
 */
export const changeNovelHomeIsUsed = async (homeDataId: string) => {
  const res = await request(`${API_PREFIX_AI_NOVEL}/UpdateHomeUsage`, {
    method: 'POST',
    body: transformObjectDeep({ homeDataId }, 'snake'),
  });
  return res;
};

/**
 * 删除配置信息
 */
export const deleteNovelHome = async (id: string) => {
  const res = await request(`${API_PREFIX_AI_NOVEL}/BatchDelHomeData`, {
    method: 'POST',
    body: { ids: [id] },
  });
  return res;
};

/**
 * 拉取整体详情接口
 */
export const getNovelHomeInfo = async (id: string) => {
  const res = await request<CgiResp<INovelHome>>(`${API_PREFIX_AI_NOVEL}/GetHomeDataDetail`, {
    method: 'POST',
    body: {
      id,
    },
  });
  if (res.code !== CgiErrorCode.NO_ERROR) {
    throw new Error(res.message || '信息拉取失败，请稍后重试');
  }
  const turnData = transformObjectDeep(res?.data, 'camel', true) as INovelHome;
  return turnData;
};

interface IGetNovelListReq {
  page: number,
  size: number,
  userId?: string,
  novelName?: string,
}
/**
 * 拉取小说列表
 */
export const getNovelList = async (req: IGetNovelListReq) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/GetPublishedNovelList`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (res.code !== CgiErrorCode.NO_ERROR) {
    throw new Error(res.message || '信息拉取失败，请稍后重试');
  }
  const turnData = transformObjectDeep(res?.data, 'camel', true) as {
    list: INovel[],
    totalCnt: number,
    hasMore: boolean,
  };
  return turnData;
};

/**
 * 保存整体数据
 * @param data
 * @returns
 */
export const modifyNovelHome = async (data: Partial<INovelHome>) => {
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_AI_NOVEL}/UpsertHomeData`, {
    method: 'POST',
    body: transformObjectDeep({ data }, 'snake'),
  });
  if (res.code !== CgiErrorCode.NO_ERROR || !res.data?.id) {
    throw new Error(res.message || '保存失败，请稍后重试');
  }
  return res.data.id;
};

/**
 * 拉取Item详情接口
 */
export const getNovelHomeItemDetail = async (id: string) => {
  const res = await request<CgiResp<INovelHomeItem>>(`${API_PREFIX_AI_NOVEL}/GetHomeDataItemDetail`, {
    method: 'POST',
    body: {
      id,
    },
  });
  if (res.code !== CgiErrorCode.NO_ERROR) {
    throw new Error(res.message || '信息拉取失败，请稍后重试');
  }
  const turnData = transformObjectDeep(res?.data, 'camel', true) as INovelHomeItem;
  return turnData;
};

/**
 * 保存单个Item信息
 */
export const modifyNovelHomeItemDetail = async (data: Partial<INovelHomeItem>) => {
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_AI_NOVEL}/UpsertHomeDataItem`, {
    method: 'POST',
    body: transformObjectDeep({ data }, 'snake'),
  });
  if (res.code !== CgiErrorCode.NO_ERROR || !res.data?.id) {
    throw new Error(res.message || '保存失败，请稍后重试');
  }
  return res.data.id;
};

enum SortRule {
  DESC = 0,
  ASC
}

export const fetchUGCNovelList = async ({
  page = 1,
  size = 20,
  status = [],
  state = [],
  name,
  novelSearchType,
  release = false,
  channelId,
  channelType,
  novelSortType,
  worldId,
  pageContext,
}: {
  page?: number;
  size?: number;
  name?: string;
  novelSearchType?: NovelSearchType;
  sortRule?: SortRule;
  status?: AuditStatusType[];
  state?: NovelState[];
  release?: boolean;
  channelId?: string;
  channelType?: ChannelCheckedType;
  novelSortType?: ChannelNovelSortType;
  worldId?: string;
  pageContext?: Record<string, string>;
}): Promise<{
  list: INovel[],
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  },
  hasMore: boolean;
  pageContext?: Record<string, string>;
}> => {
  const search_data = name ? {
    type: novelSearchType,
    content: name,
  } : {};
  const req: Record<string, any> = {
    page,
    size,
    novelName: novelSearchType === NovelSearchType.GETNOVELSEARCHTYPE_NOVEL_NAME ? name : '',
    sortConfig: {
      sortType: Number(novelSortType),
    },
    auditStatus: status?.map(v => Number(v)),
    state: state?.map(v => Number(v)),
    channelId,
    channelType,
    search_data,
    worldId,
  };
  // 仅公开作品下，已选择世界+重力排序时，传入 page_context
  if (release && pageContext && Object.keys(pageContext).length > 0) {
    req.page_context = pageContext;
  }
  const url = release ? `${API_PREFIX_AI_NOVEL}/GetPublishedNovelList` : `${API_PREFIX_AI_NOVEL}/GetNovelList`;
  const res = await request<CgiResp>(url, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (res.code !== CgiErrorCode.NO_ERROR) {
    MessagePlugin.error(res.message || '信息拉取失败，请稍后重试');
    return {
      list: [],
      pageInfo: {
        total: 0,
        current: 1,
        pageSize: 50,
      },
      hasMore: false,
    };
  }
  const turnData = transformObjectDeep(res?.data, 'camel', true) as {
    list: INovel[],
    pageinfo: {
      total: number;
      page: number;
      size: number;
    },
    hasMore: boolean,
    pageContext?: Record<string, string>,
  };
  return {
    list: turnData.list,
    pageInfo: {
      total: Number(turnData?.pageinfo?.total) || 0,
      current: Number(turnData?.pageinfo?.page) || 1,
      pageSize: Number(turnData?.pageinfo?.size) || 50,
    },
    hasMore: turnData?.hasMore ?? false,
    pageContext: turnData?.pageContext,
  };
};

export enum NovelState {
  EMPTY = 0,
  INIT,
  WRITING,
  FINISHED,
  ERROR,
  FAIL,
}

export interface ISeqInfo {
  seqIdx: number;
  content: string;
  allBranchs?: string[];
  chosenBranch?: string;
  state: NovelState;
  imgUrl?: string
}

interface IGetNovelReq {
  userId?: string;
  novelId: string;
}

export interface IAuthorInfo {
  id: string;
  name: string;
  avatar: RichMediaValue;
  briefInfo: string;
}

export interface IGetNovelRsp {
  novelId?: string;
  state?: NovelState;
  inspiration?: string;
  type?: NovelType;
  novelSpaceId?: string;
  novelSpaceName?: string;
  spaceCoverUrl?: string;
  novelCoverUrl?: string;
  novelName?: string;
  seqs: ISeqInfo[];
  total: number;
  createdTime: string;
  bgm: RichMediaValue;
  isRead: boolean;
  firstSeqImgUrl?: string;
  authorInfo?: IAuthorInfo;
  derivedNovelId?: string;
  channelTags?: {
    id: string;
    name: string;
  }[]
  intro?: string;
  channelIsChecked?: boolean;
}

export const getNovel = async (req: IGetNovelReq) => {
  const res = await request<CgiResp<IGetNovelRsp>>(`${API_PREFIX_AI_NOVEL}/GetNovel`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  const novel = transformObjectDeep(res?.data, 'camel', true) as IGetNovelRsp;
  return {
    ...novel,
    seqs: novel.seqs.sort((a, b) => a.seqIdx - b.seqIdx),
    code: res?.code,
    message: res?.msg,
  };
};

export const withdrawNovel = async (req: {
  novelId: string;
}) => {
  const res = await request<CgiResp<IGetNovelRsp>>(`${API_PREFIX_AI_NOVEL}/TakeDownNovel`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  return {
    code: res?.code,
    message: res?.msg,
  };
};

/**
 * 更新小说热度（pv）
 */
export const setNovelPv = async (req: { id: string; value: number }) => {
  const res = await request<CgiResp>(`${API_PREFIX_AI_NOVEL}/SetNovelPv`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (res.code !== CgiErrorCode.NO_ERROR) {
    throw new Error(res.message || '修改失败，请稍后重试');
  }
  return res;
};

export const fetchNovelChannelList = async () => {
  const res = await request<CgiResp<{
    tags: {
      id: string;
      name: string;
    }[]
  }>>(`${API_PREFIX_AI}/GetChannelTagList`, {
    method: 'POST',
  });
  const novel = transformObjectDeep(res?.data, 'camel', true) as {
    tags: {
      id: string;
      name: string;
    }[]
  };
  return {
    code: res?.code,
    message: res?.msg,
    data: novel.tags,
  };
};

export const setNovelChannel = async (params: {
  novelId: string;
  channelIds: string[];
  channelIsChecked: boolean;
}) => {
  const res = await request<CgiResp<{
    tags: {
      id: string;
      name: string;
    }[]
  }>>(`${API_PREFIX_AI_NOVEL}/SetNovelChannel`, {
    method: 'POST',
    body: transformObjectDeep(params, 'snake'),
  });

  return {
    code: res?.code,
    message: res?.msg,
  };
};

